import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Mail, Search } from "lucide-react";
import {
  listEmailJobs, approveEmailJob, rejectEmailJob, previewEmailJob,
  bulkApproveEmailJobs, bulkRejectEmailJobs, EmailJob,
} from "../api/api";
import Layout from "../components/Layout";
import { Pill } from "../components/ui/pill";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FilterDropdown } from "../components/ui/filter-dropdown";
import { EmptyState } from "../components/ui/empty-state";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "../lib/toast";
import { cn } from "@/lib/utils";

type StatusTab = "pending" | "capped";

const TAB_COPY: Record<StatusTab, { label: string; subtitle: string; empty: string; actionTitle: string }> = {
  pending: {
    label: "Wacht op goedkeuring",
    subtitle: "E-mails die wachten op jouw inhoudelijke goedkeuring",
    empty: "Geen e-mails wachten op goedkeuring",
    actionTitle: "Goedkeuren",
  },
  capped: {
    label: "Dagelijkse limiet bereikt",
    subtitle: "E-mails die de dagelijkse verzendlimiet van de ontvanger hebben geraakt",
    empty: "Geen e-mails geraakt door de dagelijkse limiet",
    actionTitle: "Alsnog verzenden",
  },
};

const EMAIL_TYPE_OPTIONS = [
  { value: "alle", label: "Alle types" },
  { value: "new_vacature", label: "Nieuwe vacature" },
  { value: "stale_vacature", label: "Verouderde vacature" },
  { value: "status_change", label: "Statuswijziging" },
];

function emailTypeLabel(type: string): string {
  return EMAIL_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

// Converts a <input type="date"> value (YYYY-MM-DD, local) to an RFC3339
// timestamp at the start/end of that local day, for the created_from/
// created_to API filters.
function startOfDayIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toISOString();
}
function endOfDayIso(dateStr: string): string {
  return new Date(`${dateStr}T23:59:59.999`).toISOString();
}

// The bulk endpoints accept at most 500 IDs per request.
const BULK_CHUNK_SIZE = 500;

async function inChunks(
  ids: number[],
  fn: (chunk: number[]) => Promise<{ affected: number }>,
): Promise<{ affected: number }> {
  let affected = 0;
  for (let i = 0; i < ids.length; i += BULK_CHUNK_SIZE) {
    affected += (await fn(ids.slice(i, i + BULK_CHUNK_SIZE))).affected;
  }
  return { affected };
}

// True on lg+ screens, where the preview renders inline next to the list
// instead of in a Dialog.
function useIsWide(): boolean {
  const query = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setWide(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return wide;
}

export default function MailGoedkeuring() {
  const qc = useQueryClient();
  const isWide = useIsWide();
  const [statusTab, setStatusTab] = useState<StatusTab>("pending");
  const [page, setPage] = useState(1);
  const [previewJob, setPreviewJob] = useState<EmailJob | null>(null);
  const [rejectingIds, setRejectingIds] = useState<number[] | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailType, setEmailType] = useState("alle");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");

  const [selectingAll, setSelectingAll] = useState(false);

  const limit = 25;
  const copy = TAB_COPY[statusTab];

  const filters = {
    approvalStatus: statusTab,
    recipientEmail: recipientEmail || undefined,
    emailType: emailType !== "alle" ? emailType : undefined,
    createdFrom: createdFrom ? startOfDayIso(createdFrom) : undefined,
    createdTo: createdTo ? endOfDayIso(createdTo) : undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "emails", statusTab, page, recipientEmail, emailType, createdFrom, createdTo],
    queryFn: () => listEmailJobs(page, limit, filters),
  });

  // Bumped whenever the filters change, so an in-flight selectAll() started
  // under the old filters discards its result instead of selecting stale IDs.
  const selectAllRun = useRef(0);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    selectAllRun.current++;
    setSelectingAll(false);
  }, [statusTab, recipientEmail, emailType, createdFrom, createdTo]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page]);

  function selectTab(tab: StatusTab) {
    setStatusTab(tab);
  }

  const approveMut = useMutation({
    mutationFn: (id: number) => approveEmailJob(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "emails"] }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => rejectEmailJob(id, reason),
    onSuccess: () => { setRejectingIds(null); setRejectReason(""); },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "emails"] }),
  });

  const bulkApproveMut = useMutation({
    mutationFn: (ids: number[]) => inChunks(ids, bulkApproveEmailJobs),
    onSuccess: (result, ids) => {
      setSelectedIds(new Set());
      if (result.affected < ids.length) toast(`${result.affected} van ${ids.length} goedgekeurd`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "emails"] }),
  });

  const bulkRejectMut = useMutation({
    mutationFn: ({ ids, reason }: { ids: number[]; reason?: string }) =>
      inChunks(ids, (chunk) => bulkRejectEmailJobs(chunk, reason)),
    onSuccess: (result, { ids }) => {
      setSelectedIds(new Set());
      setRejectingIds(null);
      setRejectReason("");
      if (result.affected < ids.length) toast(`${result.affected} van ${ids.length} afgewezen`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "emails"] }),
  });

  const jobs = data?.data ?? [];
  const total = data?.total ?? 0;
  const allOnPageSelected = jobs.length > 0 && jobs.every((j) => selectedIds.has(j.id));

  // Drop the preview once its mail leaves the current list (approved,
  // rejected, filtered out or on another page).
  useEffect(() => {
    if (previewJob && data && !data.data.some((j) => j.id === previewJob.id)) setPreviewJob(null);
  }, [data, previewJob]);

  function handlePreview(job: EmailJob) {
    setPreviewJob(job);
  }

  function toggleSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAllOnPage() {
    setSelectedIds((prev) => {
      if (allOnPageSelected) {
        const next = new Set(prev);
        jobs.forEach((j) => next.delete(j.id));
        return next;
      }
      const next = new Set(prev);
      jobs.forEach((j) => next.add(j.id));
      return next;
    });
  }

  // Selects every mail matching the current tab and filters, across all
  // pages, by paging through the list endpoint at its 200-per-page maximum.
  async function selectAll() {
    const run = ++selectAllRun.current;
    setSelectingAll(true);
    try {
      const pageSize = 200;
      const ids: number[] = [];
      for (let p = 1; ; p++) {
        const res = await listEmailJobs(p, pageSize, filters);
        if (run !== selectAllRun.current) return;
        res.data.forEach((j) => ids.push(j.id));
        if (res.data.length < pageSize || p * pageSize >= res.total) break;
      }
      setSelectedIds(new Set(ids));
    } catch {
      if (run === selectAllRun.current) toast("Kon niet alle e-mails selecteren");
    } finally {
      if (run === selectAllRun.current) setSelectingAll(false);
    }
  }

  function handleBulkApprove() {
    bulkApproveMut.mutate(Array.from(selectedIds));
  }

  function handleBulkReject() {
    setRejectingIds(Array.from(selectedIds));
  }

  function submitReject() {
    if (!rejectingIds) return;
    const reason = rejectReason || undefined;
    if (rejectingIds.length === 1) {
      rejectMut.mutate({ id: rejectingIds[0], reason });
    } else {
      bulkRejectMut.mutate({ ids: rejectingIds, reason });
    }
  }

  function closeReject() {
    setRejectingIds(null);
    setRejectReason("");
  }

  const rejectPending = rejectMut.isPending || bulkRejectMut.isPending;
  const hasDateFilter = createdFrom !== "" || createdTo !== "";

  const preview = previewJob && (
    <EmailPreview
      job={previewJob}
      statusTab={statusTab}
      approveLabel={statusTab === "pending" ? "Goedkeuren en versturen" : copy.actionTitle}
      approving={approveMut.isPending}
      onApprove={() => approveMut.mutate(previewJob.id)}
      onReject={() => setRejectingIds([previewJob.id])}
    />
  );

  return (
    <Layout title="Mail goedkeuring" subtitle={copy.subtitle}>
      <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
        <Tabs value={statusTab} onValueChange={(v) => selectTab(v as StatusTab)}>
          <TabsList>
            {(Object.keys(TAB_COPY) as StatusTab[]).map((tab) => (
              <TabsTrigger key={tab} value={tab}>{TAB_COPY[tab].label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-10 w-full items-center gap-2 rounded-pill border border-waves-line bg-white px-4 sm:w-[260px]">
            <Search size={15} className="shrink-0 text-waves-muted" />
            <input
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="Zoek op ontvanger…"
              aria-label="Zoek op ontvanger"
              className="min-w-0 flex-1 bg-transparent text-sm text-waves-ink outline-none placeholder:text-waves-muted"
            />
          </div>
          <FilterDropdown aria-label="E-mailtype" value={emailType} onChange={setEmailType} options={EMAIL_TYPE_OPTIONS} />
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={createdFrom}
              onChange={(e) => setCreatedFrom(e.target.value)}
              title="Vanaf"
              aria-label="Vanaf"
              className={cn(
                "h-10 rounded-pill border bg-white px-4 font-mono text-[13px] text-waves-ink outline-none focus-visible:border-waves-zee focus-visible:ring-2 focus-visible:ring-waves-zee/25",
                createdFrom ? "border-waves-ink" : "border-waves-line",
              )}
            />
            <span className="text-[13px] text-waves-muted">t/m</span>
            <input
              type="date"
              value={createdTo}
              onChange={(e) => setCreatedTo(e.target.value)}
              title="Tot en met"
              aria-label="Tot en met"
              className={cn(
                "h-10 rounded-pill border bg-white px-4 font-mono text-[13px] text-waves-ink outline-none focus-visible:border-waves-zee focus-visible:ring-2 focus-visible:ring-waves-zee/25",
                createdTo ? "border-waves-ink" : "border-waves-line",
              )}
            />
            {hasDateFilter && (
              <button
                onClick={() => { setCreatedFrom(""); setCreatedTo(""); }}
                className="px-1 text-[13px] font-bold text-waves-muted hover:text-waves-ink"
              >
                Wis datums
              </button>
            )}
          </div>
          <div className="ml-auto font-mono text-[13px] text-waves-muted">{total} mails</div>
        </div>

        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-[16px] border border-waves-line bg-waves-mist px-5 py-3">
            <span className="text-sm font-bold text-waves-ink">
              <span className="font-mono">{selectedIds.size}</span> geselecteerd
            </span>
            {selectedIds.size < total && (
              <Button size="sm" variant="outline" onClick={selectAll} disabled={selectingAll}>
                {selectingAll ? "Selecteren…" : <>Selecteer alle (<span className="font-mono">{total}</span>)</>}
              </Button>
            )}
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-1 text-[13px] font-bold text-waves-muted hover:text-waves-ink"
            >
              Selectie wissen
            </button>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="ink" onClick={handleBulkApprove} disabled={bulkApproveMut.isPending || selectingAll}>
                {copy.actionTitle}
              </Button>
              <Button size="sm" variant="destructive" onClick={handleBulkReject} disabled={selectingAll}>
                Afwijzen
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
          <div className="flex min-w-0 flex-col gap-3">
            <div className="overflow-hidden rounded-card border border-waves-line bg-white">
              <div className="flex items-center gap-3 border-b border-waves-line px-5 py-3.5">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={toggleSelectAllOnPage}
                  disabled={jobs.length === 0}
                  aria-label="Selecteer alles op deze pagina"
                />
                <span className="text-[11px] font-bold uppercase tracking-label text-waves-muted">Ontvanger</span>
                <span className="ml-auto text-[11px] font-bold uppercase tracking-label text-waves-muted">Aangemaakt</span>
              </div>

              {isLoading && <div className="px-6 py-11 text-center text-sm text-waves-muted">Laden…</div>}
              {!isLoading && jobs.length === 0 && (
                <EmptyState icon={<Mail />} title={copy.empty} />
              )}
              {jobs.map((job, i) => {
                const active = previewJob?.id === job.id;
                return (
                  <div
                    key={job.id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={active}
                    onClick={() => handlePreview(job)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handlePreview(job); }
                    }}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 px-5 py-4 transition-colors focus-visible:outline-none",
                      i > 0 && "border-t border-waves-line",
                      active ? "bg-waves-mist shadow-[inset_3px_0_0_theme(colors.waves.zee)]" : "hover:bg-waves-subtle focus-visible:bg-waves-mist",
                    )}
                  >
                    <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(job.id)}
                        onCheckedChange={() => toggleSelected(job.id)}
                        aria-label={`Selecteer ${job.recipient_email}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-bold text-waves-ink">{job.recipient_name}</div>
                      <div className="truncate text-[13px] text-waves-muted">{job.recipient_email}</div>
                      <div className="mt-1.5 text-[13px] font-medium text-waves-body">{emailTypeLabel(job.email_type)}</div>
                    </div>
                    <div className="shrink-0 text-right font-mono text-[12px] text-waves-muted">
                      {new Date(job.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                );
              })}
            </div>

            {total > limit && (
              <div className="flex items-center justify-between">
                <span className="font-mono text-[13px] text-waves-muted">{total} resultaten</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft /> Vorige
                  </Button>
                  <span className="px-2 font-mono text-[13px] text-waves-body">{page} / {Math.ceil(total / limit)}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page * limit >= total}>
                    Volgende <ChevronRight />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {isWide && (
            <div className="min-w-0">
              <div className="sticky top-0 rounded-card border border-waves-line bg-white p-6">
                {preview ?? (
                  <EmptyState
                    icon={<Mail />}
                    title="Selecteer een e-mail"
                    description="Kies links een e-mail om het voorbeeld te bekijken en hem goed te keuren of af te wijzen."
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {!isWide && (
        <Dialog open={!!previewJob} onOpenChange={(open) => { if (!open) setPreviewJob(null); }}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogTitle className="sr-only">E-mail voorbeeld</DialogTitle>
            {preview}
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!rejectingIds} onOpenChange={(open) => { if (!open) closeReject(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {rejectingIds && rejectingIds.length > 1 ? `${rejectingIds.length} e-mails afwijzen` : "E-mail afwijzen"}
            </DialogTitle>
            <DialogDescription>
              {rejectingIds && rejectingIds.length > 1
                ? "Deze e-mails worden nooit verstuurd. Reden (optioneel, wordt op alle geselecteerde e-mails toegepast):"
                : "Deze e-mail wordt nooit verstuurd. Reden (optioneel):"}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            aria-label="Reden"
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeReject}>Annuleren</Button>
            <Button variant="destructive" onClick={submitReject} disabled={rejectPending}>Afwijzen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function EmailPreview({
  job, statusTab, approveLabel, approving, onApprove, onReject,
}: {
  job: EmailJob;
  statusTab: StatusTab;
  approveLabel: string;
  approving: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "emails", job.id, "preview"],
    queryFn: () => previewEmailJob(job.id, { silenceErrors: true }),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 pr-10 lg:pr-0">
        <div className="min-w-0 flex-1">
          <div className="text-lg font-black tracking-[-0.01em] text-waves-ink">{data?.subject ?? "Voorbeeld"}</div>
          <div className="mt-0.5 truncate text-sm text-waves-muted">Aan: {job.recipient_email}</div>
          <div className="mt-1 font-mono text-[12px] text-waves-muted">
            {emailTypeLabel(job.email_type)} · {new Date(job.created_at).toLocaleString("nl-NL")}
          </div>
        </div>
        {statusTab === "capped"
          ? <Pill kind="capped">Dagelijkse limiet</Pill>
          : <Pill kind="hold">In afwachting</Pill>}
      </div>

      <div className="overflow-hidden rounded-nav border border-waves-line bg-waves-subtle">
        {isLoading && <div className="p-5 text-sm text-waves-muted">Laden…</div>}
        {error && (
          <div className="m-4 rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">
            Kon voorbeeld niet laden
          </div>
        )}
        {data && (
          <iframe
            title="E-mail voorbeeld"
            sandbox=""
            srcDoc={data.html}
            className="h-[60vh] w-full border-0 bg-white"
          />
        )}
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="destructive" onClick={onReject}>Afwijzen</Button>
        <Button onClick={onApprove} disabled={approving}>{approveLabel}</Button>
      </div>
    </div>
  );
}
